import { Alert, Button, FileButton, Group, Stack, Text } from "@mantine/core";
import { IconCamera, IconRefresh, IconUpload } from "@tabler/icons-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { canvasToJpegFile, compressImageFile } from "../lib/compressImage";
import { axioTokens } from "../theme";

type CaptureFacing = "user" | "environment";

interface DocumentCaptureProps {
  label: string;
  hint?: string;
  facing: CaptureFacing;
  filled?: boolean;
  uploading?: boolean;
  previewUrl?: string | null;
  onFile: (file: File) => void | Promise<void>;
}

function isCoarsePointer(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches;
}

export function DocumentCapture({
  label,
  hint,
  facing,
  filled,
  uploading,
  previewUrl,
  onFile,
}: DocumentCaptureProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [webcamOpen, setWebcamOpen] = useState(false);
  const [webcamError, setWebcamError] = useState<string | null>(null);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const mobile = isCoarsePointer();
  const showWebcam = !mobile && facing === "user";

  const stopWebcam = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setWebcamOpen(false);
  }, []);

  useEffect(() => () => stopWebcam(), [stopWebcam]);

  useEffect(() => {
    return () => {
      if (localPreview) URL.revokeObjectURL(localPreview);
    };
  }, [localPreview]);

  async function startWebcam() {
    setWebcamError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      setWebcamOpen(true);
      requestAnimationFrame(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      });
    } catch {
      setWebcamError("Camera access was blocked. Upload a photo instead.");
    }
  }

  async function captureWebcam() {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    const file = await canvasToJpegFile(canvas, `${facing}-capture.jpg`);
    stopWebcam();
    await handleFile(file);
  }

  async function handleFile(raw: File) {
    const file = await compressImageFile(raw);
    if (localPreview) URL.revokeObjectURL(localPreview);
    setLocalPreview(URL.createObjectURL(file));
    await onFile(file);
  }

  const shownPreview = localPreview ?? previewUrl ?? null;

  return (
    <Stack gap="sm">
      <div>
        <Text fw="var(--axio-font-weight-semibold)" size="sm">
          {label}
        </Text>
        {hint && (
          <Text size="xs" c="dimmed" mt="var(--axio-unit-2)">
            {hint}
          </Text>
        )}
      </div>

      {shownPreview && (
        <img
          src={shownPreview}
          alt={`${label} preview`}
          style={{
            width: "var(--axio-percentage-full)",
            maxHeight: "var(--axio-unit-240)",
            objectFit: "contain",
            borderRadius: "var(--axio-radius)",
            background: axioTokens.mediaBackground,
          }}
        />
      )}

      {webcamOpen && (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{ width: "var(--axio-percentage-full)", maxHeight: "var(--axio-unit-280)", borderRadius: axioTokens.radius.md, background: axioTokens.cameraBackdrop }}
        />
      )}

      {webcamError && (
        <Alert color="yellow" variant="light">
          {webcamError}
        </Alert>
      )}

      <Group gap="xs">
        {mobile ? (
          <FileButton
            accept="image/*"
            onChange={(file) => file && void handleFile(file)}
            inputProps={{ capture: facing }}
          >
            {(props) => (
              <Button
                {...props}
                leftSection={<IconCamera size="var(--axio-unit-16)" />}
                loading={uploading}
                variant={filled ? "light" : "filled"}
                color="violet"
              >
                {filled ? "Retake with camera" : "Take photo"}
              </Button>
            )}
          </FileButton>
        ) : showWebcam ? (
          webcamOpen ? (
            <>
              <Button color="violet" leftSection={<IconCamera size="var(--axio-unit-16)" />} onClick={() => void captureWebcam()}>
                Capture
              </Button>
              <Button variant="default" onClick={stopWebcam}>
                Cancel camera
              </Button>
            </>
          ) : (
            <Button
              variant={filled ? "light" : "filled"}
              color="violet"
              leftSection={<IconCamera size="var(--axio-unit-16)" />}
              onClick={() => void startWebcam()}
              loading={uploading}
            >
              {filled ? "Retake with camera" : "Use camera"}
            </Button>
          )
        ) : null}

        <FileButton accept="image/jpeg,image/png,image/webp,image/*" onChange={(file) => file && void handleFile(file)}>
          {(props) => (
            <Button
              {...props}
              variant="default"
              leftSection={filled ? <IconRefresh size="var(--axio-unit-16)" /> : <IconUpload size="var(--axio-unit-16)" />}
              loading={uploading}
            >
              {filled ? "Replace file" : "Upload instead"}
            </Button>
          )}
        </FileButton>
      </Group>
    </Stack>
  );
}
